import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CaseFileZipTaskStatus } from "@/briefpoint-client/models";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import FileExplorer from "@/containers/FileExplorer";
import UploadMore from "@/containers/UploadMore/UploadMore";
import WithHeldFileLists from "@/containers/WithHeldFileLists/WithHeldFileLists";
import WithheldTabButton from "@/containers/WithHeldFileLists/WithheldTabButton";
import { useCaseFileApi } from "@/hooks/useApi";
import { FILE_EXPLORER_VARIANTS, SEARCH_PARAMS } from "@/lib/constants";
import { cn, normalizeError, triggerDownloadViaIframe } from "@/lib/utils";
import { toast } from "../ui/toast";
import styles from "./CaseProductionTabs.module.scss";

const DocHandlerTabs = () => {
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();

  const urlParams = useParams<{
    id: string;
  }>();

  const [shouldPoll, setShouldPoll] = useState(false);

  // Read current view from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const currentTab =
    searchParams.get(SEARCH_PARAMS.LABEL.SUB_TAB) ||
    SEARCH_PARAMS.LABEL.PRODUCE;
  const showWithheldTab = currentTab === SEARCH_PARAMS.LABEL.WITHHELD;

  const handleTabChange = (showWithheld: boolean) => {
    // Create new search params from current location to preserve all existing parameters
    const newSearchParams = new URLSearchParams(location.search);

    // Update the view parameter
    newSearchParams.set(
      SEARCH_PARAMS.LABEL.SUB_TAB,
      showWithheld ? SEARCH_PARAMS.LABEL.WITHHELD : SEARCH_PARAMS.LABEL.PRODUCE,
    );

    // Used for toggling between produce/withheld tabs - push used to allow user to navigate in/out of those parameters when set
    history.push({
      pathname: location.pathname,
      search: `?${newSearchParams.toString()}`,
    });
  };

  // Polling query to check zip task status
  const zipTaskQueryKey = useMemo(
    () => ["caseFileApi.caseFilePollZipTask", { caseId: urlParams.id }],
    [urlParams.id],
  );

  const zipTaskPollQuery = useQuery({
    queryKey: zipTaskQueryKey,
    queryFn: () => {
      if (!urlParams.id) {
        throw new Error("ID is required");
      }
      return caseFileApi.caseFilePollZipTask({ caseId: urlParams.id });
    },
    enabled: shouldPoll && !!urlParams.id,
    retry: false,
    refetchInterval: (query) => {
      if (!shouldPoll) return false;
      const data = query.state.data;
      // Stop polling when status is Complete (200) or Error (300)
      if (
        data?.status === CaseFileZipTaskStatus.Complete ||
        data?.status === CaseFileZipTaskStatus.Error
      ) {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  // Mutation to start the zip task
  const startZipTaskMutation = useMutation({
    mutationFn: async () => {
      if (!urlParams.id) {
        throw new Error("ID is required");
      }
      return caseFileApi.caseFileStartZipTask({ caseId: urlParams.id });
    },
    onSuccess: () => {
      setShouldPoll(true);
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Error starting zip task. Please try again.");
    },
  });

  // Handle download when zip task is complete
  useEffect(() => {
    const zipView = zipTaskPollQuery.data;
    if (
      zipView?.status === CaseFileZipTaskStatus.Complete &&
      zipView.zipUrls &&
      zipView.zipUrls.length > 0
    ) {
      // Stop polling
      setShouldPoll(false);

      for (const zipUrl of zipView.zipUrls) {
        if (zipUrl.url) {
          triggerDownloadViaIframe(zipUrl.url);
        }
      }

      // We clear up the query cache
      queryClient.removeQueries({
        queryKey: zipTaskQueryKey,
      });
    } else if (zipView?.status === CaseFileZipTaskStatus.Error) {
      setShouldPoll(false);
      toast.error("Error creating zip file. Please try again.");
      queryClient.removeQueries({
        queryKey: zipTaskQueryKey,
      });
    }
  }, [zipTaskPollQuery.data, zipTaskQueryKey, queryClient]);

  const isDownloading =
    shouldPoll ||
    startZipTaskMutation.isPending ||
    zipTaskPollQuery.isFetching ||
    zipTaskPollQuery.data?.status === CaseFileZipTaskStatus.Processing;

  return (
    <>
      <div className="d-flex justify-content-between mt-2">
        <div className="d-flex">
          <div className="rounded-3 border border-gray-300 p-1">
            <WithheldTabButton
              isActive={!showWithheldTab}
              onClick={() => handleTabChange(false)}
            >
              Produce
            </WithheldTabButton>
            <WithheldTabButton
              isActive={showWithheldTab}
              onClick={() => handleTabChange(true)}
            >
              Withheld
            </WithheldTabButton>
          </div>
          <div className={styles.zipDownloadBtn}>
            <Button
              type="button"
              variant="link"
              size="sm"
              className={cn(isDownloading && styles.zipDownloadLinkProgress)}
              onClick={() => startZipTaskMutation.mutate()}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Spinner size="sm" />
                  Preparing Download...
                </>
              ) : (
                "Download"
              )}
            </Button>
          </div>
        </div>
        <UploadMore />
      </div>
      {showWithheldTab ? (
        <WithHeldFileLists />
      ) : (
        <FileExplorer variant={FILE_EXPLORER_VARIANTS.CASE} />
      )}
    </>
  );
};

export default DocHandlerTabs;
